import { Component, forwardRef, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import * as Moment from "moment";
import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { takeUntil, sample } from 'rxjs/operators';

@Component({
    selector: 'time-picker',
    styles: [`
        .big-icon {
            font-size: 35px
        }
    `],
    template: `        
        <div fxLayout="row">
            <div fxLayout="column">
                <button mat-button (click)="incrementHour()" [disabled]="disabled">
                    <i class="big-icon fa fa-sort-up"></i>
                </button>
                <input matInput
                       #hourInput
                       type="text"
                       style="text-align: center;"
                       [formControl]="hourControl"
                       (keyup.ArrowUp)="incrementHour()"
                       (keyup.ArrowDown)="decrementHour()">
                <button mat-button (click)="decrementHour()" [disabled]="disabled">
                    <i class="big-icon fa fa-sort-down"></i>
                </button>
            </div>
            <div fxLayout="column">
                <button mat-button (click)="incrementMinute()" [disabled]="disabled">
                    <i class="big-icon fa fa-sort-up"></i>
                </button>
                <input matInput
                       #minuteInput
                       type="text"
                       style="text-align: center;"
                       [formControl]="minuteControl"
                       (keyup.ArrowUp)="incrementMinute()"
                       (keyup.ArrowDown)="decrementMinute()">
                <button mat-button (click)="decrementMinute()"  [disabled]="disabled">
                    <i class="big-icon fa fa-sort-down"></i>
                </button>
            </div>
            <button mat-button (click)="toggleMeridiem()" [disabled]="disabled">{{meridiem}}</button>
        </div>
    `,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => TimePickerComponent),
        multi: true
    }]
})
export class TimePickerComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
    @ViewChild('hourInput', { static: true }) hourInputEl: ElementRef;
    @ViewChild('minuteInput', { static: true }) minuteInputEl: ElementRef;
    private goodHour$ = new BehaviorSubject(null);
    private goodMinute$ = new BehaviorSubject(null);
    private cleanupTime = new Subject<void>();
    disabled: boolean;
    private data: any;
    hourControl = new FormControl();
    hourTest = /(^0?[1-9]$)|(^1[0-2]$)/;
    minuteControl = new FormControl();
    minuteTest = /^[0-5]?[0-9]$/;
    meridiem: string;

    private propagateChange = (_: any) => { };

    ngAfterViewInit(): void {
        this.hourControl.valueChanges
        .pipe(
            sample(fromEvent(this.hourInputEl.nativeElement, 'blur')),
            takeUntil(this.cleanupTime)
        )
        .subscribe(value => {
            const prev = this.goodHour$.value;
            if (prev !== value) {
                if (this.hourTest.test(value)) {
                    this.data.hours(parseInt(value));
                    this.goodHour$.next(value);
                    this.propagateChange(this.data.toISOString());
                } else {               // non strict equality on purpose
                    this.hourControl.setValue(prev == 0 ? '12' : prev, { emitEvent: false });
                }
            }
        });
        
        this.minuteControl.valueChanges
        .pipe(
            sample(fromEvent(this.minuteInputEl.nativeElement, 'blur')),
            takeUntil(this.cleanupTime)
        )
        .subscribe((value) => {
            const prev = this.goodMinute$.value;
            if (prev !== value) {
                if (this.minuteTest.test(value)) {
                    this.data.minutes(parseInt(value));
                    this.goodMinute$.next(value);
                    this.propagateChange(this.data.toISOString());
                } else {
                    this.minuteControl.setValue(prev, { emitEvent: false });
                }
            }
        });
    }

    incrementHour(){
        const val = this.hourControl.value; 
        this.processHour(this.padTimeComponent(val === '12' ? '01' : String(parseInt(val) + 1)));
    }

    decrementHour(){
        const val = this.hourControl.value;
        this.processHour(this.padTimeComponent(val === '01' || val === '1'? '12' : String(parseInt(val) - 1)));
    }

    processHour(formattedHour) {
        let hourOfDay = parseInt(formattedHour);
        if(this.meridiem === 'PM') {
            hourOfDay += 12;
        } else if (hourOfDay === 12) {
            hourOfDay = 0;
        }
        this.data.hours(hourOfDay);
        this.hourControl.setValue(formattedHour);
        this.goodHour$.next(this.hourControl.value);
        this.propagateChange(this.data.toISOString());
    }

    incrementMinute(){
        const val = this.minuteControl.value;
        this.minuteControl.setValue(this.padTimeComponent(val === '59' ? '00' : String(parseInt(val) + 1)), { emitEvent: false });
        this.data.minutes(parseInt(this.minuteControl.value));
        this.goodMinute$.next(this.minuteControl.value);
        this.propagateChange(this.data.toISOString());
    }

    decrementMinute(){
        const val = this.minuteControl.value;
        this.minuteControl.setValue(this.padTimeComponent(val === '00' ? '59' : String(parseInt(val) - 1)), { emitEvent: false });
        this.data.minutes(parseInt(this.minuteControl.value));
        this.goodMinute$.next(this.minuteControl.value);
        this.propagateChange(this.data.toISOString());
    }

    toggleMeridiem() {
        this.data.add(12, 'hours');
        this.meridiem = this.data.format('A');
        this.propagateChange(this.data.toISOString());
    }

    private padTimeComponent = (item: string) => item.length === 1 ? '0' + item : item;
    
    private normalizeHours = hours => this.padTimeComponent(String(hours > 12 ? hours - 12 : hours));

    writeValue(obj: any) : void {
        const isFirst = !!this.data;
        this.data = Moment(obj || undefined);
        this.hourControl.setValue(this.normalizeHours(this.data.hours() || 12), { emitEvent: false });
        this.minuteControl.setValue(this.padTimeComponent(String(this.data.minutes())), { emitEvent: false });
        this.meridiem = this.data.format('A');
        if (isFirst) {
            this.goodHour$.next(`${this.data.hours()}`);
            this.goodMinute$.next(`${this.data.minutes()}`);
        }
    };

    registerOnChange(fn: any) : void {
        this.propagateChange = fn;
    };

    registerOnTouched(fn: any) : void {
    };

    setDisabledState(isDisabled: boolean) : void {
        this.disabled = isDisabled;
        if (isDisabled) {
            this.hourControl.disable();
            this.minuteControl.disable();
        } else {
            this.hourControl.enable();
            this.minuteControl.enable();
        }
    };

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
        this.goodHour$.complete();
        this.goodMinute$.complete();
    }
}
